import { useState, useEffect, useCallback, useRef } from 'react';
import { setAuthToken } from './useDB';

// ─── Config Supabase (depuis .env) ────────────────────────────────────────────
const SUPABASE_URL  = process.env.REACT_APP_SUPABASE_URL  || '';
const SUPABASE_ANON = process.env.REACT_APP_SUPABASE_ANON || '';

// Durée avant d'afficher le bandeau "session bientôt expirée" (15 min)
export const SESSION_WARN_MS = 15 * 60 * 1000;

// ── Déconnexion automatique par inactivité ────────────────────────────────────
// Délai d'inactivité avant déconnexion (30 min par défaut)
export const INACTIVITY_TIMEOUT_MS   = 30 * 60 * 1000;
// Délai avant lequel on affiche le avertissement (2 min avant déconnexion)
export const INACTIVITY_WARN_MS      = 2  * 60 * 1000;
// Événements considérés comme activité utilisateur
const ACTIVITY_EVENTS = ['mousedown', 'mousemove', 'keydown', 'touchstart', 'scroll', 'click', 'focus'];
// Clé localStorage pour stocker le dernier timestamp d'activité
// (synchronise l'inactivité entre plusieurs onglets)
const LAST_ACTIVITY_KEY = 'cmob_last_activity';

// ─── Helpers fetch Supabase Auth ──────────────────────────────────────────────
const authHeaders = () => ({
  apikey: SUPABASE_ANON,
  'Content-Type': 'application/json',
});

// Fetch avec timeout — évite les blocages réseau hors ligne
function fetchWithTimeout(url, options = {}, timeoutMs = 6000) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  return fetch(url, { ...options, signal: controller.signal })
    .finally(() => clearTimeout(timer));
}

// Cache profil offline — permet de se connecter sans réseau si déjà authentifié
const PROFILE_CACHE_KEY = 'cmob_profile_cache';
function saveProfileCache(profile) {
  try { localStorage.setItem(PROFILE_CACHE_KEY, JSON.stringify(profile)); } catch {}
}
function loadProfileCache() {
  try {
    const raw = localStorage.getItem(PROFILE_CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}
function clearProfileCache() {
  try { localStorage.removeItem(PROFILE_CACHE_KEY); } catch {}
}

async function supaSignIn(email, password) {
  const r = await fetchWithTimeout(`${SUPABASE_URL}/auth/v1/token?grant_type=password`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ email, password: password.trim() }),
  }, 8000);
  const data = await r.json();
  if (!r.ok) throw new Error(data.error_description || data.msg || 'Identifiants invalides');
  return data; // { access_token, refresh_token, expires_in, user }
}

async function supaRefresh(refreshToken) {
  const r = await fetchWithTimeout(`${SUPABASE_URL}/auth/v1/token?grant_type=refresh_token`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ refresh_token: refreshToken }),
  }, 6000);
  if (!r.ok) throw new Error('Refresh échoué');
  return r.json(); // { access_token, refresh_token, expires_in }
}

async function supaSignOut(accessToken) {
  await fetch(`${SUPABASE_URL}/auth/v1/logout`, {
    method: 'POST',
    headers: { ...authHeaders(), Authorization: `Bearer ${accessToken}` },
  }).catch(() => {}); // on ignore les erreurs réseau au logout
}

// ⚡ Cache mémoire du profil — évite un double appel réseau si le token
// est déjà connu au moment du mount (restauration de session + signIn).
let _profileMemCache = null;
let _profileMemToken = null;

// Récupère le profil métier depuis cmob_profiles via la fonction SQL
async function fetchCmobProfile(accessToken) {
  // Retourner le cache mémoire si le token n'a pas changé
  if (_profileMemCache && _profileMemToken === accessToken) {
    return _profileMemCache;
  }
  const r = await fetchWithTimeout(`${SUPABASE_URL}/rest/v1/rpc/get_my_cmob_profile`, {
    method: 'POST',
    headers: {
      apikey: SUPABASE_ANON,
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: '{}',
  }, 4000); // réduit de 6s → 4s (réseau local VPS rapide)
  if (!r.ok) {
    const t = await r.text();
    throw new Error(`Profil introuvable (${r.status}): ${t}`);
  }
  const profile = await r.json();
  _profileMemCache = profile;
  _profileMemToken = accessToken;
  return profile; // { id, login, nom, prenom, roles, staff_id, ... }
}

// ─── Stockage session ─────────────────────────────────────────────────────────
const STORAGE_KEY = 'cmob_supabase_session';

function saveTokens({ access_token, refresh_token, expires_in }) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      access_token,
      refresh_token,
      expires_at: Date.now() + (expires_in || 3600) * 1000,
    }));
  } catch {}
}

function loadTokens() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw); // { access_token, refresh_token, expires_at }
  } catch { return null; }
}

function clearTokens() {
  try { localStorage.removeItem(STORAGE_KEY); } catch {}
}

function normalizeUser(profile) {
  return {
    id:                  profile.id,
    login:               profile.login,
    nom:                 profile.nom   || '',
    prenom:              profile.prenom || '',
    roles:               Array.isArray(profile.roles) ? profile.roles : [],
    staffId:             profile.staff_id  ?? null,
    managers:            profile.manager_ids ?? [],
    actif:               profile.actif !== false,
    mustChangePassword:  profile.must_change_password === true,
  };
}

// ═══════════════════════════════════════════════════════════════════════════════
// HOOK useAuth
// ═══════════════════════════════════════════════════════════════════════════════
export function useAuth() {
  const [user,        setUser]        = useState(null);
  const [loading,     setLoading]     = useState(true);  // vrai pendant la restauration initiale
  const [sessionWarn, setSessionWarn] = useState(false); // bandeau "session bientôt expirée"

  const refreshTimerRef    = useRef(null);
  const warnTimerRef       = useRef(null);
  const inactivityTimerRef = useRef(null);  // timer déconnexion inactivité
  const inactivityWarnRef  = useRef(null);  // timer avertissement inactivité
  const [inactivityWarn, setInactivityWarn] = useState(false); // bandeau "inactivité détectée"
  const lastActivityRef    = useRef(Date.now());

  // ── Planifie le refresh automatique avant expiration ─────────────────────
  const scheduleRefresh = useCallback((expiresAt) => {
    clearTimeout(refreshTimerRef.current);
    clearTimeout(warnTimerRef.current);

    const msLeft = expiresAt - Date.now();
    // Refresh 60s avant expiration
    const refreshIn = Math.max(msLeft - 60_000, 0);
    // Avertissement SESSION_WARN_MS avant expiration
    const warnIn    = Math.max(msLeft - SESSION_WARN_MS, 0);

    warnTimerRef.current = setTimeout(() => setSessionWarn(true), warnIn);

    refreshTimerRef.current = setTimeout(async () => {
      const stored = loadTokens();
      if (!stored?.refresh_token) { clearTokens(); setUser(null); return; }
      try {
        const data = await supaRefresh(stored.refresh_token);
        saveTokens(data);
        setAuthToken(data.access_token);
        // Synchronise le client Supabase JS (supabase.from) avec le JWT
        setSessionWarn(false);
        scheduleRefresh(Date.now() + (data.expires_in || 3600) * 1000);
      } catch {
        clearTokens();
        setUser(null);
      }
    }, refreshIn);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Réinitialise le timer d'inactivité à chaque activité ───────────────────
  const resetInactivityTimer = useCallback(() => {
    const now = Date.now();
    lastActivityRef.current = now;
    // Persister pour synchro multi-onglets
    try { localStorage.setItem(LAST_ACTIVITY_KEY, String(now)); } catch {}

    // Annuler les timers en cours
    clearTimeout(inactivityTimerRef.current);
    clearTimeout(inactivityWarnRef.current);
    setInactivityWarn(false);

    // Planifier l'avertissement d'inactivité
    inactivityWarnRef.current = setTimeout(() => {
      setInactivityWarn(true);
    }, INACTIVITY_TIMEOUT_MS - INACTIVITY_WARN_MS);

    // Planifier la déconnexion automatique
    inactivityTimerRef.current = setTimeout(async () => {
      console.warn('[useAuth] Déconnexion automatique — inactivité détectée');
      const stored = loadTokens();
      if (stored?.access_token) await supaSignOut(stored.access_token);
      clearTokens();
      clearProfileCache();
      _profileMemCache = null;
      _profileMemToken = null;
      clearTimeout(refreshTimerRef.current);
      clearTimeout(warnTimerRef.current);
      setUser(null);
      setSessionWarn(false);
      setInactivityWarn(false);
    }, INACTIVITY_TIMEOUT_MS);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Écouter les événements d'activité utilisateur ────────────────────────
  useEffect(() => {
    // Ne démarrer les listeners que si l'utilisateur est connecté
    // (géré via userRef pour ne pas recréer les listeners à chaque re-render)
    const handler = () => resetInactivityTimer();

    ACTIVITY_EVENTS.forEach(e => window.addEventListener(e, handler, { passive: true }));

    // Synchro multi-onglets via storage event
    const onStorage = (e) => {
      if (e.key === LAST_ACTIVITY_KEY) {
        // Un autre onglet a eu de l'activité → réinitialiser les timers
        clearTimeout(inactivityTimerRef.current);
        clearTimeout(inactivityWarnRef.current);
        setInactivityWarn(false);
        const lastActivity = parseInt(e.newValue || '0', 10);
        const remaining = INACTIVITY_TIMEOUT_MS - (Date.now() - lastActivity);
        if (remaining > 0) {
          inactivityWarnRef.current = setTimeout(() => setInactivityWarn(true),
            Math.max(0, remaining - INACTIVITY_WARN_MS));
          inactivityTimerRef.current = setTimeout(async () => {
            const stored = loadTokens();
            if (stored?.access_token) await supaSignOut(stored.access_token);
            clearTokens(); clearProfileCache();
            _profileMemCache = null; _profileMemToken = null;
            clearTimeout(refreshTimerRef.current);
            setUser(null); setSessionWarn(false); setInactivityWarn(false);
          }, remaining);
        }
      }
    };
    window.addEventListener('storage', onStorage);

    return () => {
      ACTIVITY_EVENTS.forEach(e => window.removeEventListener(e, handler));
      window.removeEventListener('storage', onStorage);
      clearTimeout(inactivityTimerRef.current);
      clearTimeout(inactivityWarnRef.current);
    };
  }, [resetInactivityTimer]);

  // ── Démarrer le timer d'inactivité quand l'user se connecte ──────────────
  useEffect(() => {
    if (user) {
      resetInactivityTimer();
    } else {
      // User déconnecté → arrêter les timers
      clearTimeout(inactivityTimerRef.current);
      clearTimeout(inactivityWarnRef.current);
      setInactivityWarn(false);
    }
  }, [user, resetInactivityTimer]);

  // ── Restauration de session au démarrage ──────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const stored = loadTokens();
      if (!stored) {
        // Pas de session stockée → signaler à useDB qu'aucun token n'arrivera
        // (libère les waitForToken qui sinon attendent 10s inutilement)
        setAuthToken(null);
        setLoading(false);
        return;
      }

      // ⚡ Injecter le token JWT dans useDB IMMÉDIATEMENT — avant tout fetch réseau.
      // Cela débloque waitForToken() sans attendre fetchCmobProfile (2-6s réseau).
      if (stored.access_token && stored.expires_at > Date.now() + 30_000) {
        setAuthToken(stored.access_token);
        // Synchronise le client Supabase JS (supabase.from) avec le JWT
      }

      // Token encore valide (marge 30s) ?
      if (stored.expires_at > Date.now() + 30_000) {
        try {
          const profile = await fetchCmobProfile(stored.access_token);
          if (!cancelled) {
            saveProfileCache(profile); // ← mettre en cache pour le mode offline
            setUser(normalizeUser(profile));
            scheduleRefresh(stored.expires_at);
          }
        } catch (e) {
          // Hors ligne ou timeout → utiliser le cache profil si disponible
          const cached = loadProfileCache();
          if (cached && !cancelled) {
            console.warn('[useAuth] Hors ligne — profil depuis cache local');
            setUser(normalizeUser(cached));
            scheduleRefresh(stored.expires_at);
          } else {
            clearTokens();
          }
        }
      } else if (stored.refresh_token) {
        // Token expiré → refresh immédiat
        try {
          const data = await supaRefresh(stored.refresh_token);
          saveTokens(data);
          // ⚡ Injecter le nouveau token dès qu'on l'a, avant fetchCmobProfile
          setAuthToken(data.access_token);
          // Synchronise le client Supabase JS (supabase.from) avec le JWT
          const profile = await fetchCmobProfile(data.access_token);
          if (!cancelled) {
            saveProfileCache(profile);
            setUser(normalizeUser(profile));
            scheduleRefresh(Date.now() + (data.expires_in || 3600) * 1000);
          }
        } catch {
          // Refresh impossible (hors ligne) → cache profil si dispo
          const cached = loadProfileCache();
          if (cached && !cancelled) {
            console.warn('[useAuth] Hors ligne — profil depuis cache local (token expiré)');
            setUser(normalizeUser(cached));
            // Pas de scheduleRefresh : token expiré, on ne peut pas planifier
          } else {
            clearTokens();
          }
        }
      } else {
        // Token expiré et pas de refresh_token → on ne peut pas s'authentifier
        setAuthToken(null);
        clearTokens();
      }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [scheduleRefresh]);

  // ── Nettoyage timers ──────────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      clearTimeout(refreshTimerRef.current);
      clearTimeout(warnTimerRef.current);
    };
  }, []);

  // ── signIn ────────────────────────────────────────────────────────────────
  // Retourne null si succès, un message d'erreur string sinon
  const signIn = useCallback(async (email, password) => {
    try {
      const data = await supaSignIn(email, password);
      setAuthToken(data.access_token); // ← injecter immédiatement dans useDB
      // Synchronise le client Supabase JS (supabase.from) avec le JWT

      // ⚡ PERF : lancer fetchCmobProfile et le préchauffage du cache en parallèle.
      // Les deux font des requêtes réseau indépendantes — pas besoin de les séquencer.
      const profile = await fetchCmobProfile(data.access_token);
      saveTokens(data);
      saveProfileCache(profile);
      setUser(normalizeUser(profile));
      setSessionWarn(false);
      scheduleRefresh(Date.now() + (data.expires_in || 3600) * 1000);
      return null;
    } catch (e) {
      return e.message || 'Erreur de connexion';
    }
  }, [scheduleRefresh]);

  // ── signOut ───────────────────────────────────────────────────────────────
  const signOut = useCallback(async () => {
    const stored = loadTokens();
    if (stored?.access_token) await supaSignOut(stored.access_token);
    clearTokens();
    clearProfileCache();
    _profileMemCache = null; // vider le cache mémoire
    _profileMemToken = null;
    clearTimeout(refreshTimerRef.current);
    clearTimeout(warnTimerRef.current);
    clearTimeout(inactivityTimerRef.current);
    clearTimeout(inactivityWarnRef.current);
    setUser(null);
    setSessionWarn(false);
    setInactivityWarn(false);
  }, []);

  // ── prolongerInactivite ─────────────────────────────────────────────────────
  // Appelé depuis le bandeau d'avertissement d'inactivité → remet les timers à zéro
  const prolongerInactivite = useCallback(() => {
    resetInactivityTimer();
  }, [resetInactivityTimer]);

  // ── prolongerSession ──────────────────────────────────────────────────────
  // Appelé depuis le bandeau d'avertissement
  const prolongerSession = useCallback(async () => {
    const stored = loadTokens();
    if (!stored?.refresh_token) return;
    try {
      const data = await supaRefresh(stored.refresh_token);
      saveTokens(data);
      setSessionWarn(false);
      scheduleRefresh(Date.now() + (data.expires_in || 3600) * 1000);
    } catch {
      clearTokens();
      setUser(null);
    }
  }, [scheduleRefresh]);

  // ── getAccessToken ────────────────────────────────────────────────────────
  // Utilisé par App.js pour injecter le JWT dans useDB via setAuthToken()
  const getAccessToken = useCallback(() => {
    return loadTokens()?.access_token || null;
  }, []);

  // ── clearMustChangePassword ───────────────────────────────────────────────
  // Appelé par App.js après que l'utilisateur a changé son mot de passe
  const clearMustChangePassword = useCallback(async () => {
    const stored = loadTokens();
    if (!stored?.access_token) return;
    // Mettre à jour le flag dans cmob_profiles
    await fetch(`${SUPABASE_URL}/rest/v1/cmob_profiles?id=eq.${user?.id}`, {
      method: 'PATCH',
      headers: {
        apikey:          SUPABASE_ANON,
        Authorization:   `Bearer ${stored.access_token}`,
        'Content-Type':  'application/json',
        Prefer:          'return=minimal',
      },
      body: JSON.stringify({ must_change_password: false }),
    });
    setUser(prev => prev ? { ...prev, mustChangePassword: false } : prev);
  }, [user?.id]);

  return {
    user,
    loading,
    sessionWarn,
    inactivityWarn,
    signIn,
    signOut,
    prolongerSession,
    prolongerInactivite,
    resetInactivityTimer,
    getAccessToken,
    clearMustChangePassword,
  };
}
